/*#
# Ambient light (b3d-light)

Hemispheric ambient fill light. Wraps `BABYLON.HemisphericLight`. Cheap to render
and never casts shadows — pair it with `b3dSun` for directional light that
does. Drop intensity around 0.3 when you want the sun's shadows to actually
read; keep it higher (around 0.7) when you don't have a sun and want
everything visible.

## Demo

```js
import { b3d, b3dLight, b3dSkybox, b3dGround, b3dSphere, label3d, slider3d } from 'tosijs-3d'
import { orbitCam } from 'tosijs-3d/demo-utils'
import { tosi } from 'tosijs'

const { ambient } = tosi({ ambient: { intensity: 0.6 } })

preview.append(
  b3d(
    {
      panelTitle: 'Light',
      scenePanel: () => [
        slider3d({ label: 'ambient', value: ambient.intensity, min: 0, max: 1.5, step: 0.05 }),
      ],
      sceneCreated(el, BABYLON) {
        const camera = orbitCam(el, {
          alpha: -Math.PI / 2.5, beta: Math.PI / 3, radius: 6,
          target: [0, 1, 0], maxElevationDeg: 70,
        })
        camera.lowerRadiusLimit = 2.5
        camera.upperRadiusLimit = 18
      },
    },
    b3dLight({ intensity: ambient.intensity }),
    b3dSkybox({ timeOfDay: 10 }),
    b3dGround({ width: 10, height: 10, color: '#556644' }),
    b3dSphere({ y: 1, diameter: 1.5, color: '#4488cc' }),
  ),
)
```
```css
tosi-b3d { width: 100%; height: 100%; }
```

## Attributes

| Attribute | Default | Description |
|-----------|---------|-------------|
| `x` | `0` | Light direction X (the "up" direction) |
| `y` | `1` | Light direction Y |
| `z` | `0` | Light direction Z |
| `intensity` | `1` | Brightness multiplier |
| `diffuse` | `'#ffffff'` | Diffuse color (hex) |
| `specular` | `'#808080'` | Specular color (hex) |
| `groundColor` | `'#000000'` | Bounce from below (hex). Babylon's default is black, which is why vertical faces look unlit however high `intensity` goes — set a dim version of your ground colour |
| `nightIntensity` | `-1` | With a day/night `b3dSkybox`: the brightness at night, eased from `intensity` as the sky's `daylight` goes. `-1` keeps `intensity` all day and night |

A fixed ambient light under a day/night sky makes midnight look like an
overcast noon; give it a `nightIntensity` and the fill falls with the sun,
while the sun itself becomes the moon.
*/
/*{ "parent": "sky-and-light", "order": 30 }*/

import { B3dChild } from './b3d-utils.js'
import { sceneDaylight } from './light-pool.js'
import * as BABYLON from '@babylonjs/core'
import type { B3d } from './tosi-b3d.js'

export class B3dLight extends B3dChild {
  static preferredTagName = 'tosi-b3d-light'

  static initAttributes = {
    x: 0,
    y: 1,
    z: 0,
    intensity: 1,
    diffuse: '#ffffff',
    specular: '#808080',
    /*
    WHAT COMES UP FROM THE GROUND — Babylon's default is BLACK, and that is
    why an ambient-lit scene has vertical faces that read as unlit however far
    you push `intensity`. A hemispheric light is cosine-weighted about its own
    direction, so a wall gets almost nothing from the sky half and everything
    it does get comes from this. A dim, desaturated version of the ground's
    colour is the honest value; pure black is a scene lit from a hole.
    */
    groundColor: '#000000',
    nightIntensity: -1,
  }

  owner: B3d | null = null
  light?: BABYLON.HemisphericLight
  private follow: BABYLON.Observer<BABYLON.Scene> | null = null

  sceneReady(owner: B3d, scene: BABYLON.Scene) {
    this.owner = owner
    const attrs = this as any
    this.light = new BABYLON.HemisphericLight(
      'light',
      new BABYLON.Vector3(attrs.x, attrs.y, attrs.z),
      scene
    )
    // Its attributes now: `render()` may have run before the light existed,
    // and nothing re-runs it, which left every b3dLight at Babylon's defaults
    // (intensity 1, a black ground colour) whatever it was given.
    this.render()
    owner.register({ lights: [this.light] })
    this.follow = scene.onBeforeRenderObservable.add(() => {
      const attrs = this as any
      const night = Number(attrs.nightIntensity)
      if (this.light == null || !(night >= 0)) return
      const day = Number(attrs.intensity)
      this.light.intensity = night + (day - night) * sceneDaylight(this.owner)
    })
  }

  sceneDispose() {
    if (this.follow)
      this.owner?.scene?.onBeforeRenderObservable.remove(this.follow)
    this.follow = null
    if (this.light != null) {
      this.light.dispose()
      this.light = undefined
    }
    this.owner = null
  }

  render() {
    super.render()
    if (this.light != null) {
      const attrs = this as any
      this.light.direction = new BABYLON.Vector3(attrs.x, attrs.y, attrs.z)
      this.light.intensity = attrs.intensity
      this.light.diffuse = BABYLON.Color3.FromHexString(attrs.diffuse)
      this.light.specular = BABYLON.Color3.FromHexString(attrs.specular)
      this.light.groundColor = BABYLON.Color3.FromHexString(attrs.groundColor)
    }
  }
}

export const b3dLight = B3dLight.elementCreator()
